import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    Brain, MessageSquare, BookOpen, ArrowRight,
    Moon, SunMedium, ShieldCheck, Users, ChevronRight
} from "lucide-react";
import { Routes, Route, Link, Outlet } from "react-router-dom";
import AdminInbox from "./pages/AdminInbox";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { LoginPage, SignupPage, AccountPage } from "./pages/AuthPages";
import EditableText from "./components/EditableText";
import { db } from "./auth/firebaseClient";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { t } from "./i18n/translations";
import { LanguageContext, useLanguage } from "./i18n/LanguageContext";

/* ------------------ APP (ROUTES) ------------------ */
export default function App() {
    return (
        <AuthProvider>
            <Routes>
                <Route element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="services" element={<ServicesPage />} />
                    <Route path="team" element={<TeamPage />} />
                    <Route path="resources" element={<ResourcesPage />} />
                    <Route path="contact" element={<ContactPage />} />
                    <Route path="privacy" element={<PrivacyPage />} />
                    <Route path="terms" element={<TermsPage />} />
                    <Route path="accessibility" element={<AccessibilityPage />} />
                    <Route path="login" element={<LoginPage />} />
                    <Route path="signup" element={<SignupPage />} />
                    <Route path="account" element={<AccountPage />} />
                    <Route path="admin/inbox" element={<AdminInbox />} />
                    <Route path="*" element={<NotFound />} />
                </Route>
            </Routes>
        </AuthProvider>
    );
}

/* ------------------ LAYOUT (theme + header/footer) ------------------ */
function Layout() {
    const [theme, setTheme] = useState("light");
    const [language, setLanguage] = useState("en");
    const dir = language === "ar" || language === "he" ? "rtl" : "ltr";

    // load saved theme
    useEffect(() => {
        const saved = localStorage.getItem("theme");
        if (saved === "dark" || saved === "light") setTheme(saved);
    }, []);

    // load saved language
    useEffect(() => {
        const saved = localStorage.getItem("language");
        if (saved === "en" || saved === "ar" || saved === "he") {
            setLanguage(saved);
        }
    }, []);

    // apply to <html>
    useEffect(() => {
        const root = document.documentElement;
        root.classList.toggle("dark", theme === "dark");
        localStorage.setItem("theme", theme);
    }, [theme]);

    // update document direction and language preference
    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute("dir", dir);
        root.setAttribute("lang", language);
        localStorage.setItem("language", language);
    }, [dir, language]);

    return (
        <LanguageContext.Provider value={{ language, setLanguage }}>
            <div
                dir={dir}
                className="min-h-screen bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900 text-slate-900 dark:text-slate-100"
            >
                <SiteHeader
                    theme={theme}
                    setTheme={setTheme}
                    language={language}
                    setLanguage={setLanguage}
                />
                <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <Outlet />
                </main>
                <SiteFooter />
            </div>
        </LanguageContext.Provider>
    );
}

/* ------------------ HEADER ------------------ */
function SiteHeader({ theme, setTheme, language, setLanguage }) {
    const { currentUser, logout } = useAuth();

    return (
        <header className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Brain className="h-5 w-5" />
          </span>
                    <span className="text-lg">{t("brand", language)}</span>
                </Link>

                <nav className="hidden md:flex items-center gap-6 text-sm">
                    <Link className="hover:text-primary transition-colors" to="/#about">{t("nav.about", language)}</Link>
                    <Link className="hover:text-primary transition-colors" to="/services">{t("nav.services", language)}</Link>
                    <Link className="hover:text-primary transition-colors" to="/team">{t("nav.team", language)}</Link>
                    <Link className="hover:text-primary transition-colors" to="/resources">{t("nav.resources", language)}</Link>
                    <Link className="hover:text-primary transition-colors" to="/contact">{t("nav.contact", language)}</Link>

                    {/* Admin-only link */}
                    {currentUser?.role === "admin" && (
                        <Link className="hover:text-primary transition-colors" to="/admin/inbox">
                            {t("nav.inbox", language)}
                        </Link>
                    )}
                </nav>

                <div className="flex items-center gap-2">
                    <LanguageSelect language={language} setLanguage={setLanguage} />
                    <ThemeToggle theme={theme} setTheme={setTheme} />

                    {currentUser ? (
                        <div className="flex items-center gap-2">
                            <Button asChild size="sm" className="rounded-2xl">
                                <Link to="/account" className="inline-flex items-center gap-2">
                                    {t(currentUser.role === "admin" ? "auth.admin" : "auth.myAccount", language)}
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                            </Button>
                            <button
                                type="button"
                                onClick={logout}
                                className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                            >
                                {t("auth.signout", language)}
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Button asChild size="sm" className="rounded-2xl">
                                <Link to="/login">{t("auth.login", language)}</Link>
                            </Button>
                            <Button asChild size="sm" className="rounded-2xl">
                                <Link to="/signup">{t("auth.signup", language)}</Link>
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}


function LanguageSelect({ language, setLanguage }) {
    const label = t("language.select", language);
    const names = t("language.names", language) || {};
    return (
        <select
            aria-label={label}
            value={language}
            onChange={(event) => {
                const value = event.target.value;
                if (value === "en" || value === "ar" || value === "he") {
                    setLanguage(value);
                }
            }}
            className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
            <option value="en">{names.en || "English"}</option>
            <option value="ar">{names.ar || "العربية"}</option>
            <option value="he">{names.he || "עברית"}</option>
        </select>
    );
}

function ThemeToggle({ theme, setTheme }) {
    const isDark = theme === "dark";
    const Icon = isDark ? Moon : SunMedium;
    const { language } = useLanguage();
    const label = isDark ? t("theme.switchToLight", language) : t("theme.switchToDark", language);
    return (
        <button
            aria-label={label}
            onClick={() => setTheme(isDark ? "light" : "dark")}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
            title={label}
        >
            <Icon className="h-4 w-4 text-slate-700 dark:text-slate-200" />
        </button>
    );
}

/* ------------------ PAGES ------------------ */
// Home: your original sections
function Home() {
    return (
        <>
            <Hero />
            <TrustedBar />
            <About />
            <Services />
            <Team />
            <Resources />
        </>
    );
}

function ServicesPage() {
    const { language } = useLanguage();
    return (
        <section className="py-16">
            <SectionTitle
                eyebrow={t("services.eyebrow", language)}
                title={t("services.allTitle", language)}
                copy={t("services.allCopy", language)}
            />
            <div className="mt-8">
                <Services />
            </div>
        </section>
    );
}

function TeamPage() {
    const { language } = useLanguage();
    return (
        <section className="py-0">
            <div className="mt-8">
                <Team />
            </div>
        </section>
    );
}

function ResourcesPage() {
    return (
            <div className="mt-8">
                <Resources/>
            </div>
    );
}

function ContactPage() {
    const { language } = useLanguage();
    const [pending, setPending] = useState(false);
    const [status, setStatus] = useState({ ok: false, err: "" });
    const defaultError = "__default_contact_error__";

    async function onSubmit(event) {
        event.preventDefault();
        setStatus({ ok: false, err: "" });

        const formEl = event.currentTarget;            // capture BEFORE any await
        const form = new FormData(formEl);

        const payload = {
            name: String(form.get("name") || "").trim(),
            email: String(form.get("email") || "").trim().toLowerCase(),
            subject: String(form.get("subject") || "").trim(),
            message: String(form.get("message") || "").trim(),
            createdAt: serverTimestamp(),
        };

        setPending(true);
        try {
            await addDoc(collection(db, "contactMessages"), payload);
            formEl.reset();                               // use captured element
            setStatus({ ok: true, err: "" });
        } catch (error) {
            console.error("[contact] submit failed:", error);
            setStatus({ ok: false, err: error?.message || defaultError });
        } finally {
            setPending(false);
        }
    }


    return (
        <section className="py-16">
            <SectionTitle
                eyebrow={t("contact.eyebrow", language)}
                title={t("contact.title", language)}
                copy={t("contact.copy", language)}
            />
            <div className="mt-8 grid gap-6 md:grid-cols-2">
                <Card className="rounded-2xl">
                    <CardHeader><CardTitle className="text-base">{t("contact.sendAMessage", language)}</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                        {status.ok ? (
                            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
                                {t("contact.ok", language)}
                            </div>
                        ) : null}
                        {status.err ? (
                            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-2 text-sm text-red-600">
                                {status.err === defaultError ? t("contact.err", language) : status.err}
                            </div>
                        ) : null}
                        <form className="grid gap-4" onSubmit={onSubmit}>
                            <Input name="name" placeholder={t("contact.name", language)} required />
                            <Input name="email" type="email" placeholder={t("contact.email", language)} required />
                            <Input name="subject" placeholder={t("contact.subject", language)} />
                            <Textarea name="message" rows={5} placeholder={t("contact.message", language)} required />
                            <Button type="submit" className="rounded-2xl" disabled={pending}>
                                {pending ? t("contact.sending", language) : t("contact.send", language)}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                <Card className="rounded-2xl">
                    <CardHeader><CardTitle className="text-base">{t("contact.howWeWork", language)}</CardTitle></CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                        {(t("contact.bullets", language) || []).map((bullet) => (
                            <p key={bullet}>• {bullet}</p>
                        ))}
                    </CardContent>
                </Card>
            </div>
        </section>
    );
}

function DocumentPage({ translationKey }) {
    const { language } = useLanguage();
    const rawContent = t(translationKey, language);
    const fallbackContent = t(translationKey, "en");
    const content =
        rawContent && typeof rawContent === "object"
            ? rawContent
            : fallbackContent && typeof fallbackContent === "object"
              ? fallbackContent
              : null;

    if (!content) {
        return null;
    }

    const introParagraphs = Array.isArray(content.intro)
        ? content.intro
        : content.intro
          ? [content.intro]
          : [];
    const sections = Array.isArray(content.sections) ? content.sections : [];
    const contact = content.contact && typeof content.contact === "object" ? content.contact : null;

    const renderParagraphs = (paragraphs, keyPrefix) =>
        paragraphs.map((text, index) => (
            <p key={`${keyPrefix}-${index}`} className="text-sm leading-relaxed text-muted-foreground">
                {text}
            </p>
        ));

    return (
        <section className="py-16 sm:py-24">
            <div className="mx-auto max-w-3xl space-y-10">
                <header className="space-y-3">
                    <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{content.title}</h1>
                    {content.updated && <p className="text-sm text-muted-foreground">{content.updated}</p>}
                    {renderParagraphs(introParagraphs, "intro")}
                </header>

                <div className="space-y-6">
                    {sections.map((section, index) => {
                        const bodyParagraphs = Array.isArray(section.body)
                            ? section.body
                            : section.body
                              ? [section.body]
                              : [];
                        const items = Array.isArray(section.items) ? section.items : [];
                        return (
                            <article
                                key={`section-${index}`}
                                className="space-y-4 rounded-2xl border border-slate-200/60 bg-white/80 p-6 shadow-sm dark:border-slate-800/60 dark:bg-slate-900/60"
                            >
                                <div className="space-y-3">
                                    <h2 className="text-xl font-semibold tracking-tight">{section.title}</h2>
                                    {renderParagraphs(bodyParagraphs, `section-${index}-body`)}
                                    {items.length > 0 && (
                                        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
                                            {items.map((item, itemIndex) => (
                                                <li key={`section-${index}-item-${itemIndex}`}>{item}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {section.note && (
                                        <p className="text-xs font-medium text-primary/80">{section.note}</p>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>

                {contact && (
                    <div className="space-y-3 rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-6">
                        <h2 className="text-lg font-semibold tracking-tight">{contact.title}</h2>
                        {renderParagraphs(
                            Array.isArray(contact.body)
                                ? contact.body
                                : contact.body
                                  ? [contact.body]
                                  : [],
                            "contact-body"
                        )}
                        {contact.email && (
                            <a
                                href={`mailto:${contact.email}`}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80"
                            >
                                {contact.email}
                                <ArrowRight className="h-4 w-4" />
                            </a>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

function PrivacyPage() {
    return <DocumentPage translationKey="privacyPage" />;
}

function TermsPage() {
    return <DocumentPage translationKey="termsPage" />;
}

function AccessibilityPage() {
    return <DocumentPage translationKey="accessibilityPage" />;
}

function NotFound() {
    const { language } = useLanguage();
    return (
        <section className="py-16">
            <h1 className="text-2xl font-semibold">{t("notFound.title", language)}</h1>
            <p className="mt-2 text-muted-foreground">{t("notFound.copy", language)}</p>
            <div className="mt-6">
                <Button asChild><Link to="/">{t("notFound.goHome", language)}</Link></Button>
            </div>
        </section>
    );
}

/* ------------------ SECTIONS (used by pages) ------------------ */
function Hero() {
    const { language } = useLanguage();
    const rawFeatures = t("hero.features", language);
    const features = Array.isArray(rawFeatures) ? rawFeatures : [];
    const featureIcons = [MessageSquare, ShieldCheck, Users];
    return (
        <section className="py-16 sm:py-24">
            <div className="grid items-center gap-10 md:grid-cols-2">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <EditableText translationKey="hero.title">
                        {(value) => (
                            <h1 className="text-4xl sm:text-5xl/tight font-semibold tracking-tight">{value}</h1>
                        )}
                    </EditableText>
                    <EditableText translationKey="hero.body" multiline>
                        {(value) => <p className="mt-4 text-muted-foreground text-lg">{value}</p>}
                    </EditableText>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <EditableText translationKey="hero.cta">
                            {(value) => (
                                <Button asChild size="lg" className="rounded-2xl">
                                    <Link to="/services" className="inline-flex items-center gap-2">
                                        {value} <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </Button>
                            )}
                        </EditableText>
                        <EditableText translationKey="hero.explore">
                            {(value) => (
                                <Link to="/resources" className="inline-flex items-center gap-2 text-sm hover:text-primary transition-colors">
                                    <ChevronRight className="h-4 w-4" /> {value}
                                </Link>
                            )}
                        </EditableText>
                    </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
                    <div className="relative isolate overflow-hidden rounded-2xl border bg-gradient-to-br from-primary/10 via-transparent to-transparent p-1">
                        <div className="rounded-[1rem] bg-background/80 backdrop-blur p-6">
                            <ul className="grid gap-4 text-sm">
                                {features.map((_, index) => {
                                    const Icon = featureIcons[index] || MessageSquare;
                                    return (
                                        <li key={`feature-${index}`} className="flex items-start gap-3 rounded-2xl border p-4">
                                            <Icon className="mt-0.5 h-5 w-5 text-primary" />
                                            <div>
                                                <EditableText translationKey={`hero.features.${index}.title`}>
                                                    {(value) => <div className="font-medium">{value}</div>}
                                                </EditableText>
                                                <EditableText translationKey={`hero.features.${index}.text`} multiline>
                                                    {(value) => <div className="text-muted-foreground">{value}</div>}
                                                </EditableText>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function TrustedBar() {
    const { language } = useLanguage();
    const rawHighlights = t("highlights", language);
    const highlights = Array.isArray(rawHighlights) ? rawHighlights : [];
    return (
        <section aria-label="Highlights" className="py-4">
            <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-slate-600 dark:text-slate-300">
                {highlights.map((_, index) => (
                    <EditableText key={`highlight-${index}`} translationKey={`highlights.${index}`}>
                        {(value) => <span className="opacity-80">{value}</span>}
                    </EditableText>
                ))}
            </div>
        </section>
    );
}

function SectionTitle({ eyebrow, title, copy, eyebrowKey, titleKey, copyKey }) {
    return (
        <div className="max-w-2xl">
            {eyebrowKey ? (
                <EditableText translationKey={eyebrowKey}>
                    {(value) => <div className="text-xs uppercase tracking-widest text-primary/80">{value}</div>}
                </EditableText>
            ) : eyebrow ? (
                <div className="text-xs uppercase tracking-widest text-primary/80">{eyebrow}</div>
            ) : null}
            {titleKey ? (
                <EditableText translationKey={titleKey}>
                    {(value) => <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight">{value}</h2>}
                </EditableText>
            ) : title ? (
                <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h2>
            ) : null}
            {copyKey ? (
                <EditableText translationKey={copyKey} multiline>
                    {(value) => <p className="mt-2 text-muted-foreground">{value}</p>}
                </EditableText>
            ) : copy ? (
                <p className="mt-2 text-muted-foreground">{copy}</p>
            ) : null}
        </div>
    );
}

function About() {
    const { language } = useLanguage();
    const rawCards = t("about.cards", language);
    const cards = Array.isArray(rawCards) ? rawCards : [];
    return (
        <section className="py-16 border-t">
            <SectionTitle eyebrowKey="about.eyebrow" titleKey="about.title" copyKey="about.copy" />
            <div className="mt-8 grid gap-6 md:grid-cols-3">
                {cards.map((_, index) => (
                    <Card key={`about-card-${index}`} className="rounded-2xl">
                        <CardHeader>
                            <EditableText translationKey={`about.cards.${index}.title`}>
                                {(value) => <CardTitle className="text-base">{value}</CardTitle>}
                            </EditableText>
                        </CardHeader>
                        <CardContent className="text-sm text-muted-foreground">
                            <EditableText translationKey={`about.cards.${index}.text`} multiline>
                                {(value) => <span>{value}</span>}
                            </EditableText>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </section>
    );
}

function Services() {
    const { language } = useLanguage();
    const rawItems = t("services.items", language);
    const translatedItems = Array.isArray(rawItems) ? rawItems : [];
    const icons = [MessageSquare, BookOpen, Users, ShieldCheck, BookOpen, ChevronRight];
    const items = translatedItems.map((item, index) => ({
        ...item,
        icon: icons[index] || MessageSquare,
    }));
    return (
        <section className="py-16 border-t">
            <SectionTitle eyebrowKey="services.eyebrow" titleKey="services.title" copyKey="services.copy" />
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((it, index) => {
                    const Icon = it.icon;
                    return (
                        <motion.div key={`service-${index}`} whileHover={{ y: -4 }}>
                            <Card className="rounded-2xl h-full">
                                <CardHeader className="flex flex-row items-center gap-3">
                                    <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <EditableText translationKey={`services.items.${index}.title`}>
                                        {(value) => <CardTitle className="text-base">{value}</CardTitle>}
                                    </EditableText>
                                </CardHeader>
                                <CardContent className="text-sm text-muted-foreground">
                                    <EditableText translationKey={`services.items.${index}.text`} multiline>
                                        {(value) => <span>{value}</span>}
                                    </EditableText>
                                </CardContent>
                            </Card>
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
}

function Team() {
    const { language } = useLanguage();
    const { currentUser, teamMembers, loading: authLoading } = useAuth();
    const locale = language === "ar" ? "ar" : language === "he" ? "he" : "en";
    const roster = useMemo(
        () => [...teamMembers].sort((a, b) => (a.name || "").localeCompare(b.name || "", locale)),
        [teamMembers, locale]
    );

    return (
        <section className="py-16 border-t">
            <SectionTitle eyebrowKey="team.eyebrow" titleKey="team.title" copyKey="team.copy" />
            {authLoading ? (
                <EditableText translationKey="team.loading">
                    {(value) => <div className="mt-6 text-sm text-muted-foreground">{value}</div>}
                </EditableText>
            ) : !currentUser ? (
                <EditableText translationKey="team.signInToView" multiline>
                    {(value) => (
                        <div className="mt-6 rounded-2xl border border-slate-200 bg-white/60 p-6 text-sm text-muted-foreground dark:border-slate-800 dark:bg-slate-900/60">
                            {value}
                        </div>
                    )}
                </EditableText>
            ) : roster.length === 0 ? (
                <EditableText translationKey="team.none">
                    {(value) => <div className="mt-6 text-sm text-muted-foreground">{value}</div>}
                </EditableText>
            ) : (
                <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {roster.map((member) => {
                        const initial = (member.name || "?").trim().charAt(0).toUpperCase() || "?";
                        const displayName = [member.prefix, member.name].filter(Boolean).join(" ").trim();
                        return (
                            <Card key={member.id} className="rounded-2xl">
                                <CardHeader className="pb-0">
                                    <div className="flex items-center gap-3">
                                        {member.photoURL ? (
                                            <img
                                                src={member.photoURL}
                                                alt={member.name ? `${member.name}'s avatar` : "Team member avatar"}
                                                className="h-12 w-12 rounded-full object-cover ring-2 ring-slate-100 dark:ring-slate-800"
                                            />
                                        ) : (
                                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold">
                                                {initial}
                                            </div>
                                        )}
                                        <div>
                                            <CardTitle className="text-base">{displayName || member.name || t("team.member", language)}</CardTitle>
                                            <div className="text-sm text-muted-foreground">{member.role || t("team.member", language)}</div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="text-sm space-y-2 pt-4">
                                    {member.bio ? (
                                        <div className="text-muted-foreground">{member.bio}</div>
                                    ) : null}
                                    <div className="text-muted-foreground break-all">{member.email}</div>
                                    <div className="text-xs font-medium uppercase tracking-wide">
                                        {member.approved ? t("team.approved", language) : t("team.pending", language)}
                                    </div>
                                    {member.verifiedBy ? (
                                        <div className="text-xs text-muted-foreground">{t("team.verifiedBy", language)} {member.verifiedBy}</div>
                                    ) : null}
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function Resources() {
    const { language } = useLanguage();
    const { books, resources } = useAuth();
    const locale = language === "ar" ? "ar" : language === "he" ? "he" : "en";
    const sortedBooks = useMemo(
        () => [...books].sort((a, b) => (a.title || "").localeCompare(b.title || "", locale)),
        [books, locale]
    );
    const sortedResources = useMemo(
        () => [...resources].sort((a, b) => (a.title || "").localeCompare(b.title || "", locale)),
        [resources, locale]
    );

    const renderCover = (title, url) => {
        const initial = (title || "?").trim().charAt(0).toUpperCase() || "?";
        if (url) {
            return (
                <img
                    src={url}
                    alt={`${title || ""} cover`}
                    className="h-16 w-16 rounded object-cover"
                    loading="lazy"
                    onError={(event) => {
                        event.currentTarget.style.display = "none";
                    }}
                />
            );
        }
        return (
            <div className="h-16 w-16 rounded bg-primary/10 text-primary flex items-center justify-center font-semibold">
                {initial}
            </div>
        );
    };

    const renderTags = (tags) => {
        if (!Array.isArray(tags) || tags.length === 0) return null;
        return (
            <div className="mt-2 flex flex-wrap gap-1">
                {tags.map((tag) => (
                    <span
                        key={tag}
                        className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-800/70 dark:text-slate-200"
                    >
                        {tag}
                    </span>
                ))}
            </div>
        );
    };

    const renderLink = (url) => {
        if (!url) {
            return <span className="text-sm text-muted-foreground">{t("resources.noLink", language)}</span>;
        }
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:underline">
                {t("resources.view", language)}
            </a>
        );
    };

    const countLabel = (count) => `${count} ${count === 1 ? t("resources.items", language) : t("resources.itemsPlural", language)}`;

    return (
        <section className="py-16 border-t">
            <SectionTitle eyebrowKey="resources.eyebrow" titleKey="resources.title" copyKey="resources.copy" />
            <div className="mt-8 space-y-12">
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <EditableText translationKey="resources.books">
                            {(value) => <h3 className="text-lg font-semibold">{value}</h3>}
                        </EditableText>
                        <span className="text-xs uppercase tracking-wide text-muted-foreground">{countLabel(sortedBooks.length)}</span>
                    </div>
                    {sortedBooks.length === 0 ? (
                        <EditableText translationKey="resources.noneBooks" multiline>
                            {(value) => <p className="text-sm text-muted-foreground">{value}</p>}
                        </EditableText>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {sortedBooks.map((book) => (
                                <Card key={book.id} className="rounded-2xl">
                                    <CardContent className="flex flex-col gap-3 p-4">
                                        <div className="flex items-start gap-3">
                                            {renderCover(book.title, book.coverURL)}
                                            <div className="flex-1 space-y-1">
                                                <div className="font-medium text-base">{book.title}</div>
                                                {book.author ? (
                                                    <div className="text-sm text-muted-foreground">{t("resources.by", language)} {book.author}</div>
                                                ) : null}
                                                {book.description ? (
                                                    <p className="text-sm text-muted-foreground">{book.description}</p>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            {renderLink(book.linkURL)}
                                            <span className="text-xs text-muted-foreground">{book.createdBy || ""}</span>
                                        </div>
                                        {renderTags(book.tags)}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <EditableText translationKey="resources.resources">
                            {(value) => <h3 className="text-lg font-semibold">{value}</h3>}
                        </EditableText>
                        <span className="text-xs uppercase tracking-wide text-muted-foreground">{countLabel(sortedResources.length)}</span>
                    </div>
                    {sortedResources.length === 0 ? (
                        <EditableText translationKey="resources.noneRes" multiline>
                            {(value) => <p className="text-sm text-muted-foreground">{value}</p>}
                        </EditableText>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {sortedResources.map((resource) => (
                                <Card key={resource.id} className="rounded-2xl">
                                    <CardContent className="flex flex-col gap-3 p-4">
                                        <div className="flex items-start gap-3">
                                            {renderCover(resource.title, resource.coverURL)}
                                            <div className="flex-1 space-y-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <div className="font-medium text-base">{resource.title}</div>
                                                    {resource.type ? (
                                                        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{resource.type}</span>
                                                    ) : null}
                                                </div>
                                                {resource.description ? (
                                                    <p className="text-sm text-muted-foreground">{resource.description}</p>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            {renderLink(resource.linkURL)}
                                            <span className="text-xs text-muted-foreground">{resource.createdBy || ""}</span>
                                        </div>
                                        {renderTags(resource.tags)}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

/* ------------------ FOOTER ------------------ */
function SiteFooter() {
    const { language } = useLanguage();
    return (
        <footer className="mt-16 border-t">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 text-sm text-muted-foreground flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Brain className="h-5 w-5" />
          </span>
                    <span>© {new Date().getFullYear()} {t("brand", language)}. {t("footer.rights", language)}</span>
                </div>
                <div className="flex items-center gap-6">
                    <Link to="/privacy" className="hover:text-primary">{t("footer.privacy", language)}</Link>
                    <Link to="/accessibility" className="hover:text-primary">{t("footer.accessibility", language)}</Link>
                    <Link to="/terms" className="hover:text-primary">{t("footer.terms", language)}</Link>
                </div>
            </div>
        </footer>
    );
}
